// ── Bank statement mailbox (Admin → Companies, workspace area) ─────────────────────────────────
// Which connected mailbox the AI Bank scanner reads, the passwords that open the statement PDFs and
// the accounts it never imports. Server single point: services/bankMailboxSettings.ts. The stored
// PDF passwords never come back to the browser — the card only says how many are set; entering new
// ones replaces them all.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bankscanApi, BankMailboxSettingsView, BankMailboxSettingsSave } from '../../utils/api/bankscan';

const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const hint: React.CSSProperties = { fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 };
const warn: React.CSSProperties = { fontSize: 11, color: '#B42318', marginTop: 4 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const ghost: React.CSSProperties = { padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };

export default function BankMailboxSettings() {
  const { t } = useTranslation();
  const [v, setV] = useState<BankMailboxSettingsView | null>(null);
  const [mailbox, setMailbox] = useState('');
  const [ignored, setIgnored] = useState('');
  const [pwEdit, setPwEdit] = useState(false);
  const [pwText, setPwText] = useState('');
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [saving, setSaving] = useState(false);

  const load = (d: BankMailboxSettingsView) => { setV(d); setMailbox(d.mailboxEmail || ''); setIgnored(d.ignoredAccounts || ''); };
  useEffect(() => {
    bankscanApi.get().then(d => { setV(d); setMailbox(d.mailboxEmail || ''); setIgnored(d.ignoredAccounts || ''); }).catch((e: any) => setErr(e.message));
  }, []);

  const put = async (body: BankMailboxSettingsSave) => {
    setSaving(true); setErr(''); setMsg('');
    try {
      load(await bankscanApi.save(body));
      setPwEdit(false); setPwText('');
      setMsg(t('bms_saved')); setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  const save = () => {
    const body: BankMailboxSettingsSave = { mailboxEmail: mailbox, ignoredAccounts: ignored };
    const pws = pwEdit ? pwText.split(/\r?\n/).map(s => s.trim()).filter(Boolean) : [];
    if (pws.length) body.pdfPasswords = pws;   // replace-all; an empty box changes nothing
    put(body);
  };
  const clearPasswords = () => { if (window.confirm(t('bms_pw_clear_confirm'))) put({ pdfPasswords: [] }); };

  if (!v) {
    return (
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('bms_title')}</div>
        {err && <div style={warn}>{err}</div>}
      </div>
    );
  }

  const known = v.accounts.some(a => a.email === mailbox);
  const pw = v.pdfPasswords;
  const eff = v.effective;
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('bms_title')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('bms_hint')}</div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}

      {/* Mailbox — one of the connected accounts */}
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>{t('bms_mailbox')}</label>
        <select value={mailbox} onChange={e => setMailbox(e.target.value)} style={input}>
          <option value="">{v.envMailbox ? t('bms_mailbox_env_named', { email: v.envMailbox }) : t('bms_mailbox_env')}</option>
          {mailbox && !known && <option value={mailbox}>{mailbox} ({t('bms_not_connected')})</option>}
          {v.accounts.map(a => <option key={a.email} value={a.email}>{a.email}{a.name && a.name.toLowerCase() !== a.email ? ` — ${a.name}` : ''}</option>)}
        </select>
        <div style={hint}>{t('bms_mailbox_hint')}</div>
        {eff.ready
          ? <div style={{ ...hint, color: 'var(--text-secondary)' }}><i className="ti ti-circle-check" style={{ marginRight: 4, color: '#1D9E75' }} />{eff.source === 'setting' ? t('bms_reads_setting', { email: eff.email }) : t('bms_reads_env', { email: eff.email })}</div>
          : <div style={warn}><i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />{eff.source === 'none' ? t('bms_reads_none') : eff.error}</div>}
      </div>

      {/* Statement PDF passwords — count only, replace-all */}
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>{t('bms_pw')}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: pw.source === 'none' ? '#B42318' : 'var(--text-secondary)' }}>
            <i className="ti ti-lock" style={{ marginRight: 4 }} />
            {pw.source === 'setting' ? t('bms_pw_setting', { n: pw.count }) : pw.source === 'env' ? t('bms_pw_env', { n: pw.count }) : t('bms_pw_none')}
          </span>
          <span style={{ flex: 1 }} />
          {!pwEdit && <button type="button" onClick={() => setPwEdit(true)} style={ghost}>{t('bms_pw_replace')}</button>}
          {!pwEdit && (pw.source === 'setting' || pw.unreadable) && <button type="button" onClick={clearPasswords} disabled={saving} style={{ ...ghost, color: '#B42318' }}>{t('bms_pw_clear')}</button>}
        </div>
        {pw.unreadable && <div style={warn}>{t('bms_pw_unreadable')}</div>}
        {pwEdit && (
          <div style={{ marginTop: 8 }}>
            <textarea value={pwText} onChange={e => setPwText(e.target.value)} rows={3} autoComplete="off" spellCheck={false}
              style={{ ...input, fontFamily: 'monospace', resize: 'vertical' }} placeholder={t('bms_pw_placeholder')} />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ ...hint, flex: 1 }}>{t('bms_pw_input_hint')}</div>
              <button type="button" onClick={() => { setPwEdit(false); setPwText(''); }} style={{ ...ghost, marginTop: 4 }}>{t('bms_cancel')}</button>
            </div>
          </div>
        )}
      </div>

      {/* Accounts never imported */}
      <div>
        <label style={lbl}>{t('bms_ignored')}</label>
        <input value={ignored} onChange={e => setIgnored(e.target.value)} style={input} placeholder={t('bms_ignored_placeholder')} />
        <div style={hint}>{t('bms_ignored_hint')}</div>
        {!ignored.trim() && v.ignoredEffective.source === 'env' && <div style={hint}>{t('bms_ignored_env', { n: v.ignoredEffective.count })}</div>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
        <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('bms_saving') : t('bms_save')}</button>
      </div>
    </div>
  );
}
